import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import React, { useEffect, useRef, useState } from 'react';

import { ReactComponent as Close } from '../../images/icons/close.svg';
import { ReactComponent as Arrow } from '../../images/icons/fs-lc-arrow.svg';
import RichTextConverter from '../Common/RichText/RichTextConverter';
import ContentContainer from '../PageSections/ContentContainer';
import PageSection from '../PageSections/PageSection';
import ContentfulImage from './ContentfulImage';
import * as styles from './Drawer.module.scss';
import { ArrowLink } from './Links';

/*
 *NOTE: This component is not currently in use. It was developed for the Product Analytics page before the rebrand. Holding onto it for now in case we want to repurpose.
 */

gsap.registerPlugin(ScrollTrigger);

export default function Drawer({ section }) {
  const component = useRef();
  const drawer = useRef();
  const icons = useRef([]);
  const open = useRef();
  const close = useRef();
  const previous = useRef();
  const next = useRef();

  const [activeIconIdx, setActiveIconIdx] = useState();
  const [isOpen, setIsOpen] = useState();

  useEffect(() => {
    const ctx = gsap.context(() => {
      let activeCardIdx = 0;
      let cooldown = false;

      const openToCard = (index) => {
        if (getComputedStyle(drawer.current).getPropertyValue('height') === '0px') {
          gsap.to(drawer.current, {
            height: 'auto',
            onStart: () => setIsOpen(true),
            onComplete: ScrollTrigger.refresh,
          });
        }

        window.scrollTo({ top: component.current.offsetTop + 200, behavior: 'smooth' });
        const isNext = activeCardIdx < index;
        const difference = isNext ? index - activeCardIdx : activeCardIdx - index;

        gsap.to('.card', {
          xPercent: isNext ? `-=${100 * difference}` : `+=${100 * difference}`,
          duration: 0.5,
          onStart: () => {
            cooldown = true;
            activeCardIdx = index;
            setActiveIconIdx(index);
          },
          onComplete: () => (cooldown = false),
        });
      };

      const animateSlide = (direction) => {
        const isNext = direction === 'next';
        const isLastCard = isNext && activeCardIdx >= section.content?.length - 1;
        const isFirstCard = !isNext && activeCardIdx === 0;

        if (cooldown) return;

        // Loop infinitely in both directions
        if (isFirstCard) {
          openToCard(section.content?.length - 1);
          return;
        }

        if (isLastCard) {
          openToCard(0);
          return;
        }

        gsap.to('.card', {
          xPercent: isNext ? '-=100' : '+=100',
          duration: 0.5,
          onStart: () => {
            cooldown = true;
            if (isNext) {
              setActiveIconIdx(activeCardIdx + 1);
              activeCardIdx += 1;
            } else {
              setActiveIconIdx(activeCardIdx - 1);
              activeCardIdx -= 1;
            }
          },
          onComplete: () => (cooldown = false),
        });
      };

      next.current.addEventListener('click', () => animateSlide('next'));
      previous.current.addEventListener('click', animateSlide);

      icons.current?.forEach((icon, i) => icon.addEventListener('click', () => openToCard(i)));

      open.current?.addEventListener('click', () => openToCard(0));

      const closeDrawer = () => {
        gsap.to(drawer.current, {
          height: '0px',
          onStart: () => {
            setIsOpen(false);
            setActiveIconIdx(null);
            window.scrollTo({ top: component.current.offsetTop, behavior: 'smooth' });
          },
          onComplete: ScrollTrigger.refresh,
        });
      };

      close.current.addEventListener('click', closeDrawer);
    }, [component]);

    return () => ctx.revert();
  });

  return (
    <div ref={component} className={styles.section} data-analytics-section={'drawer'}>
      <PageSection className={styles.iconsWrapper}>
        <h3>{section.heading}</h3>
        <div className={styles.icons}>
          {section.content?.map((feature, i) => (
            <button
              ref={(el) => (icons.current[i] = el)}
              key={`icon-${i}`}
              className={[styles.icon, activeIconIdx === i ? styles.active : ''].join(' ')}
            >
              <div className={styles.image}>
                <ContentfulImage imageFragment={feature.contentSection?.media?.[0]} className={styles.base} />
                <ContentfulImage imageFragment={feature.contentSection?.media?.[1]} className={styles.highlight} />
              </div>
              <div className={styles.label}>{feature.subtitle}</div>
            </button>
          ))}
        </div>
        <button className={[styles.open, isOpen ? styles.hidden : ''].join(' ')} ref={open}>
          <Close />
          {section.ctaLabel}
        </button>
      </PageSection>

      <div className={styles.drawer} ref={drawer}>
        <div className={styles.drawerWrapper}>
          <ContentContainer>
            <div className={styles.controls}>
              <button className={styles.close} ref={close} aria-label={'Close'}>
                <Close />
              </button>
              <button className={styles.previous} ref={previous} aria-label={'Previous'}>
                <Arrow />
              </button>
              <button ref={next} aria-label={'Next'}>
                <Arrow />
              </button>
            </div>
            <div className={styles.cards}>
              {section.content?.map((feature, i) => (
                <div
                  data-analytics-component={'drawer-card'}
                  data-analytics-id={feature.title}
                  key={`card-${i}`}
                  className={[styles.cardWrapper, 'card'].join(' ')}
                >
                  <div className={styles.card}>
                    <div>
                      <h2>{feature.title}</h2>
                      <RichTextConverter className={styles.content} copy={feature.copy} />
                      <div className={styles.cta}>
                        <ContentfulImage
                          className={styles.ctaIcon}
                          imageFragment={feature.contentSection?.media?.[0]}
                        />{' '}
                        <ArrowLink label={feature.ctaLabel} url={feature.ctaUrl} hoverAnimation={true} />
                      </div>
                      {feature.logo && <ContentfulImage imageFragment={feature.logo} className={styles.logo} />}
                    </div>
                    <ContentfulImage imageFragment={feature.mainImage} />
                  </div>
                </div>
              ))}
            </div>
          </ContentContainer>
        </div>
      </div>
    </div>
  );
}
